<template>
  <div class="url-upload-page">
    <div class="url-upload-container">
      <div class="upload-header">
        <div class="header-icon">🛒</div>
        <h1>淘宝リンクアップロード</h1>
        <span class="subtitle">一括 · 簡単</span>
      </div>

      <div class="stats-bar">
        <div class="stat">
          <strong>{{ totalCount }}</strong>
          <span class="label">件のリンク</span>
        </div>
        <div class="stat">
          <span class="label">有効リンク</span>
          <strong class="highlight">{{ validCount }}</strong>
        </div>
        <button class="clear-all-btn" type="button" @click="clearAll">🗑 すべてクリア</button>
      </div>

      <div class="input-area">
        <div class="section-title">
          ✏️ 手動追加
          <span class="badge">淘宝 / 天猫対応</span>
        </div>
        <div class="input-row">
          <div class="input-wrap">
            <input
              v-model.trim="linkInput"
              type="text"
              placeholder="淘宝商品リンクを貼り付け（例：https://item.taobao.com/...）"
              @keydown.enter.prevent="handleAdd"
            />
          </div>
          <button class="btn-add" type="button" @click="handleAdd">
            <span>＋</span> 追加
          </button>
        </div>
        <div class="input-hint">
          <span>💡</span> item.taobao.com / detail.tmall.com / m.tb.cn / e.tb.cn などに対応
        </div>
      </div>

      <div
        class="upload-area"
        :class="{ dragover: isDragover }"
        @click="openFileDialog"
        @dragover.prevent="isDragover = true"
        @dragleave.prevent="isDragover = false"
        @drop.prevent="handleDrop"
      >
        <span class="icon">📂</span>
        <div class="title">クリックまたはドラッグ＆ドロップで表をアップロード</div>
        <div class="desc"><strong>.csv</strong> または <strong>.xlsx</strong> ファイルに対応</div>
        <div class="formats">表には「リンク」または「url」列を含めてください</div>
        <input
          ref="fileInput"
          type="file"
          accept=".csv,.xlsx,.xls"
          @click.stop
          @change="handleFileChange"
        />
      </div>

      <div>
        <div class="list-header">
          <div class="left">
            <span class="section-title">📋 リンクリスト</span>
            <span class="count">全 {{ totalCount }} 件</span>
          </div>
          <div class="actions">
            <button type="button" @click="exportCSV">📥 CSVエクスポート</button>
            <button class="primary-btn" type="button" :disabled="submitting" @click="submitAll">
              🚀 {{ submitting ? "送信中..." : "すべて送信" }}
            </button>
          </div>
        </div>

        <div class="table-wrap">
          <div class="table-scroll">
            <table class="link-table">
              <thead>
                <tr>
                  <th class="index-col">#</th>
                  <th>リンクアドレス</th>
                  <th class="status-col">状態</th>
                  <th class="operate-col">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="links.length === 0">
                  <td colspan="4">
                    <div class="empty-state">
                      <span class="icon">📭</span>
                      <p>リンクがまだありません。手動追加または表をアップロードしてください</p>
                    </div>
                  </td>
                </tr>
                <tr v-for="(item, index) in links" :key="item.id">
                  <td><span class="index-badge">{{ index + 1 }}</span></td>
                  <td>
                    <a class="link-text" :href="item.url" target="_blank" :title="item.url">
                      {{ displayUrl(item.url) }}
                    </a>
                  </td>
                  <td><span class="status-badge">{{ statusText(item.status) }}</span></td>
                  <td class="operate-cell">
                    <button class="delete-btn" type="button" title="削除" @click="deleteLink(item.id)">✕</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="toast-container">
      <div v-for="toast in toasts" :key="toast.id" class="toast" :class="toast.type">
        {{ toast.message }}
      </div>
    </div>

    <el-dialog
      title="送信失敗"
      :visible.sync="failureDialogVisible"
      width="640px"
      custom-class="submit-failure-dialog"
    >
      <div class="failure-dialog-body">
        <div class="failure-summary">
          <strong>{{ failureDialogMessage }}</strong>
        </div>
        <div v-if="failureLinks.length" class="failure-links">
          <div class="failure-links-title">失敗したリンク</div>
          <ol>
            <li v-for="(url, index) in failureLinks" :key="index">{{ url }}</li>
          </ol>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="failureDialogVisible = false">確認</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { submitLink } from "../../../api/API/user";

const XLSX_CDN = "https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js";

export default {
  name: "UrlUpload",
  data() {
    return {
      linkInput: "",
      links: [],
      toasts: [],
      isDragover: false,
      submitting: false,
      failureDialogVisible: false,
      failureDialogMessage: "",
      failureLinks: [],
      xlsxLoading: null,
      linkHeaderNames: [
        "リンク",
        "url",
        "link",
        "商品リンク",
        "淘宝リンク",
        "アドレス",
        "商品アドレス",
        "网址",
      ],
    };
  },
  computed: {
    totalCount() {
      return this.links.length;
    },
    validCount() {
      return this.links.filter((item) => item.status === "valid").length;
    },
  },
  mounted() {
    this.showToast("👋 リンクを貼り付けるか、表をアップロードしてください", "info");
  },
  methods: {
    generateId() {
      return Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 6);
    },
    isValidTaobaoLink(url) {
      if (!url || typeof url !== "string") return false;
      const trimmed = url.trim();
      if (!trimmed) return false;
      const patterns = [
        /^https?:\/\/(?:item\.taobao\.com|detail\.tmall\.com|m\.tb\.cn|e\.tb\.cn|world\.taobao\.com|chaoshi\.detail\.tmall\.com|detail\.yao\.95095\.com|detail\.tmall\.hk|taobao\.com)/i,
        /^(https?:\/\/)?[me]\.tb\.cn\/[A-Za-z0-9.]+/i,
        /^(https?:\/\/)?(detail|item)\.(taobao|tmall)\.com/i,
        /^(https?:\/\/)?(m\.)?(taobao|tmall)\.com/i,
      ];
      return patterns.some((pattern) => pattern.test(trimmed));
    },
    normalizeUrl(url) {
      let normalized = url.trim();
      if (!/^https?:\/\//i.test(normalized)) {
        normalized = "https://" + normalized;
      }
      return normalized;
    },
    displayUrl(url) {
      return url.length > 60 ? url.slice(0, 60) + "…" : url;
    },
    statusText(status) {
      if (status === "valid") return "✅ 有効";
      if (status === "invalid") return "⚠️ 無効";
      return "⏳ 検証待ち";
    },
    createLink(url) {
      const normalized = this.normalizeUrl(url);
      return {
        id: this.generateId(),
        url: normalized,
        status: this.isValidTaobaoLink(normalized) ? "valid" : "invalid",
      };
    },
    handleAdd() {
      if (!this.linkInput) {
        this.showToast("リンクアドレスを入力してください", "warning");
        return;
      }
      const item = this.createLink(this.linkInput);
      this.links.push(item);
      this.linkInput = "";
      this.showToast(`✅ ${item.status === "valid" ? "有効" : "無効"}リンクを追加しました`, item.status === "valid" ? "success" : "warning");
    },
    deleteLink(id) {
      this.links = this.links.filter((item) => item.id !== id);
      this.showToast("リンクを削除しました", "success");
    },
    clearAll() {
      if (this.links.length === 0) {
        this.showToast("リストは既に空です", "warning");
        return;
      }
      this.$confirm("すべてのリンクをクリアしますか？", "確認", {
        confirmButtonText: "確定",
        cancelButtonText: "キャンセル",
        type: "warning",
      })
        .then(() => {
          this.links = [];
          this.showToast("すべてのリンクをクリアしました", "success");
        })
        .catch(() => {});
    },
    addLinksBatch(urls) {
      const items = urls
        .map((url) => String(url || "").trim())
        .filter(Boolean)
        .map((url) => this.createLink(url));
      if (items.length === 0) {
        this.showToast("有効なリンクデータが見つかりません", "error");
        return;
      }
      this.links = this.links.concat(items);
      this.showToast(`📥 ${items.length} 件のリンクをインポートしました`, "success");
    },
    openFileDialog() {
      this.$refs.fileInput.click();
    },
    handleFileChange(event) {
      const file = event.target.files && event.target.files[0];
      if (file) this.handleFile(file);
      event.target.value = "";
    },
    handleDrop(event) {
      this.isDragover = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) this.handleFile(file);
    },
    handleFile(file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext === "csv") {
        this.readCsv(file);
      } else if (ext === "xlsx" || ext === "xls") {
        this.readExcel(file);
      } else {
        this.showToast("対応していないファイル形式です。.csv または .xlsx をアップロードしてください", "error");
      }
    },
    readCsv(file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const rows = this.parseCsv(text).filter((row) => row.some((cell) => String(cell).trim()));
        if (rows.length < 2) {
          this.showToast("CSVファイルにはヘッダー行と少なくとも1行のデータが必要です", "error");
          return;
        }
        const linkIndex = this.findLinkColumn(rows[0]);
        if (linkIndex === -1) {
          this.showToast("「リンク」列が見つかりません。ヘッダーに「リンク」または「url」を含めてください", "error");
          return;
        }
        const urls = rows.slice(1).map((row) => row[linkIndex]).filter(Boolean);
        this.addLinksBatch(urls);
      };
      reader.onerror = () => {
        this.showToast("CSVファイルの読み込みに失敗しました", "error");
      };
      reader.readAsText(file);
    },
    parseCsv(text) {
      const rows = [];
      let row = [];
      let cell = "";
      let inQuotes = false;
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const next = text[i + 1];
        if (char === '"' && inQuotes && next === '"') {
          cell += '"';
          i++;
        } else if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === "," && !inQuotes) {
          row.push(cell.trim());
          cell = "";
        } else if ((char === "\n" || char === "\r") && !inQuotes) {
          if (char === "\r" && next === "\n") i++;
          row.push(cell.trim());
          rows.push(row);
          row = [];
          cell = "";
        } else {
          cell += char;
        }
      }
      if (cell || row.length) {
        row.push(cell.trim());
        rows.push(row);
      }
      return rows;
    },
    readExcel(file) {
      this.loadXlsx()
        .then(() => {
          const reader = new FileReader();
          reader.onload = (event) => {
            try {
              const data = new Uint8Array(event.target.result);
              const workbook = window.XLSX.read(data, { type: "array" });
              const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
              const json = window.XLSX.utils.sheet_to_json(firstSheet, { defval: "" });
              if (json.length === 0) {
                this.showToast("Excelファイルが空です", "error");
                return;
              }
              const headers = Object.keys(json[0]);
              const linkIndex = this.findLinkColumn(headers);
              if (linkIndex === -1) {
                this.showToast("「リンク」列が見つかりません。ヘッダーに「リンク」または「url」を含めてください", "error");
                return;
              }
              const linkKey = headers[linkIndex];
              const urls = json.map((row) => String(row[linkKey] || "").trim()).filter(Boolean);
              this.addLinksBatch(urls);
            } catch (error) {
              this.showToast("Excelファイルの解析に失敗しました: " + error.message, "error");
            }
          };
          reader.onerror = () => {
            this.showToast("Excelファイルの読み込みに失敗しました", "error");
          };
          reader.readAsArrayBuffer(file);
        })
        .catch(() => {
          this.showToast("Excel解析ライブラリの読み込みに失敗しました", "error");
        });
    },
    loadXlsx() {
      if (window.XLSX) return Promise.resolve();
      if (this.xlsxLoading) return this.xlsxLoading;
      this.xlsxLoading = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = XLSX_CDN;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      return this.xlsxLoading;
    },
    findLinkColumn(headers) {
      const normalizedHeaders = headers.map((header) => String(header || "").trim().toLowerCase());
      return normalizedHeaders.findIndex((header) => {
        return this.linkHeaderNames.some((name) => header.indexOf(name.toLowerCase()) !== -1);
      });
    },
    exportCSV() {
      if (this.links.length === 0) {
        this.showToast("エクスポートするリンクがありません", "warning");
        return;
      }
      let csv = "リンク,状態\n";
      this.links.forEach((item) => {
        const status = item.status === "valid" ? "有効" : item.status === "invalid" ? "無効" : "検証待ち";
        csv += `"${String(item.url).replace(/"/g, '""')}",${status}\n`;
      });
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `淘宝リンク_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      this.showToast("📥 エクスポート完了", "success");
    },
    submitAll() {
      if (this.links.length === 0) {
        this.showToast("送信するリンクがありません", "warning");
        return;
      }
      const validLinks = this.links.filter((item) => item.status === "valid");
      if (validLinks.length === 0) {
        this.showToast("有効なリンクがありません", "warning");
        return;
      }
      if (this.submitting) return;
      this.submitting = true;
      submitLink({
        data: validLinks.map((item) => ({
          url: item.url,
        })),
      })
        .then((res) => {
          const isSuccess = res && (res.success === true || res.code == 1);
          if (!isSuccess) {
            this.openSubmitFailureDialog(res, validLinks);
            return;
          }
          this.showToast(`🚀 ${validLinks.length} 件の有効リンクを送信しました`, "success");
        })
        .catch(() => {
          this.openSubmitFailureDialog({
            msg: "送信に失敗しました",
          }, validLinks);
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    openSubmitFailureDialog(res, submittedLinks) {
      this.failureDialogMessage = (res && res.msg) || "送信に失敗しました";
      const responseLinks = this.normalizeFailureLinks(res && res.data);
      this.failureLinks = responseLinks.length
        ? responseLinks
        : this.normalizeFailureLinks(submittedLinks);
      this.failureDialogVisible = true;
    },
    normalizeFailureLinks(data) {
      if (!data) return [];
      if (Array.isArray(data)) {
        return data
          .map((item) => {
            if (item && typeof item === "object") {
              return item.url || item.link || item.href || "";
            }
            return item;
          })
          .map((item) => String(item || "").trim())
          .filter(Boolean);
      }
      if (data && typeof data === "object") {
        return this.normalizeFailureLinks(data.url || data.link || data.href || "");
      }
      return [String(data).trim()].filter(Boolean);
    },
    showToast(message, type) {
      const id = this.generateId();
      this.toasts.push({ id, message, type: type || "info" });
      setTimeout(() => {
        this.toasts = this.toasts.filter((toast) => toast.id !== id);
      }, 2500);
    },
  },
};
</script>

<style lang="scss" scoped>
.url-upload-page {
  width: 100%;
  min-height: 100%;
  padding: 30px 20px;
  background: #f6f8fa;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "PingFang SC", "Microsoft YaHei", sans-serif;
  color: #1a1a2e;
}

.url-upload-container {
  --primary: #ff6a00;
  --primary-hover: #e85d00;
  --primary-light: #fff0e6;
  --bg: #f6f8fa;
  --text-primary: #1a1a2e;
  --text-secondary: #4a4a6a;
  --text-muted: #8888aa;
  --border: #e2e8f0;
  --transition: 0.25s ease;

  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 40px 44px 48px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
  transition: var(--transition);
}

.upload-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 32px;
  flex-wrap: wrap;

  .header-icon {
    width: 48px;
    height: 48px;
    background: var(--primary-light);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    flex-shrink: 0;
  }

  h1 {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: 0;
    background: linear-gradient(135deg, var(--primary), #ff8c42);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .subtitle {
    margin-left: auto;
    padding: 6px 16px;
    background: var(--bg);
    border-radius: 40px;
    color: var(--text-secondary);
    font-size: 15px;
    font-weight: 500;
  }
}

.stats-bar {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 28px;
  padding: 12px 20px;
  background: var(--bg);
  border-radius: 10px;

  .stat {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 14px;
    color: var(--text-secondary);
  }

  strong {
    color: var(--text-primary);
    font-size: 20px;
    font-weight: 700;
  }

  .highlight {
    color: var(--primary);
  }

  .clear-all-btn {
    margin-left: auto;
    padding: 4px 12px;
    border: none;
    border-radius: 6px;
    background: none;
    color: var(--text-muted);
    font-size: 13px;
    cursor: pointer;
    transition: var(--transition);

    &:hover {
      background: #fee2e2;
      color: #dc2626;
    }
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;

  .badge {
    padding: 0 10px;
    border-radius: 30px;
    background: var(--primary-light);
    color: var(--primary);
    font-size: 12px;
    font-weight: 600;
    line-height: 22px;
  }
}

.input-area {
  margin-bottom: 32px;
}

.input-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  .input-wrap {
    position: relative;
    flex: 1;
    min-width: 200px;
  }

  input {
    width: 100%;
    padding: 14px 18px;
    border: 2px solid var(--border);
    border-radius: 10px;
    background: var(--bg);
    color: var(--text-primary);
    outline: none;
    font-size: 15px;
    transition: var(--transition);
    box-sizing: border-box;

    &:focus {
      border-color: var(--primary);
      background: #fff;
      box-shadow: 0 0 0 4px rgba(255, 106, 0, 0.1);
    }

    &::placeholder {
      color: var(--text-muted);
    }
  }

  .btn-add {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 32px;
    border: none;
    border-radius: 10px;
    background: var(--primary);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: var(--transition);

    &:hover {
      background: var(--primary-hover);
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(255, 106, 0, 0.25);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

.input-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  color: var(--text-muted);
  font-size: 13px;
}

.upload-area {
  position: relative;
  margin-bottom: 32px;
  padding: 36px 20px 32px;
  border: 2px dashed var(--border);
  border-radius: 10px;
  background: #fafbfc;
  text-align: center;
  cursor: pointer;
  transition: var(--transition);

  &:hover,
  &.dragover {
    border-color: var(--primary);
    background: var(--primary-light);
  }

  &.dragover {
    transform: scale(1.01);
  }

  .icon {
    display: block;
    margin-bottom: 10px;
    font-size: 44px;
  }

  .title {
    color: var(--text-primary);
    font-size: 16px;
    font-weight: 600;
  }

  .desc {
    margin-top: 4px;
    color: var(--text-secondary);
    font-size: 14px;
  }

  .formats {
    display: inline-block;
    margin-top: 10px;
    padding: 4px 16px;
    border-radius: 30px;
    background: var(--bg);
    color: var(--text-muted);
    font-size: 13px;
  }

  input[type="file"] {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
  }
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;

  .left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .section-title {
    margin-bottom: 0;
  }

  .count {
    color: var(--text-secondary);
    font-size: 14px;
  }

  .actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  button {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 16px;
    border: 1px solid var(--border);
    border-radius: 30px;
    background: #fff;
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: var(--transition);

    &:hover {
      border-color: var(--primary);
      background: var(--primary-light);
      color: var(--primary);
    }

    &.primary-btn {
      border-color: var(--primary);
      background: var(--primary);
      color: #fff;

      &:hover {
        border-color: var(--primary-hover);
        background: var(--primary-hover);
        color: #fff;
      }
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.65;
      transform: none;
      box-shadow: none;
    }
  }
}

.table-wrap {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fff;
}

.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.link-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  thead {
    background: var(--bg);
  }

  th {
    padding: 12px 18px;
    border-bottom: 1px solid var(--border);
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0;
    text-align: left;
  }

  td {
    padding: 12px 18px;
    border-bottom: 1px solid var(--border);
    vertical-align: middle;
    word-break: break-all;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: #fafbfc;
  }

  .index-col {
    width: 50px;
  }

  .status-col {
    width: 100px;
  }

  .operate-col {
    width: 70px;
    text-align: center;
  }

  .operate-cell {
    text-align: center;
  }
}

.link-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--primary);
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.index-badge {
  display: inline-block;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--bg);
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  line-height: 26px;
  text-align: center;
}

.status-badge {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 30px;
  background: #e6f7e6;
  color: #0a7a0a;
  font-size: 12px;
  font-weight: 500;
}

.delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  background: none;
  color: var(--text-muted);
  font-size: 18px;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: #fee2e2;
    color: #dc2626;
  }
}

.empty-state {
  padding: 40px 20px;
  color: var(--text-muted);
  text-align: center;

  .icon {
    display: block;
    margin-bottom: 12px;
    font-size: 48px;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 15px;
  }
}

.toast-container {
  position: fixed;
  bottom: 30px;
  left: 50%;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  transform: translateX(-50%);
}

.toast {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 28px;
  border-radius: 40px;
  background: #1a1a2e;
  color: #fff;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  font-size: 14px;
  font-weight: 500;
  animation: slideUp 0.35s ease;
  pointer-events: auto;

  &.success {
    background: #0a7a0a;
  }

  &.error {
    background: #b91c1c;
  }

  &.warning {
    background: #b45309;
  }
}

.failure-dialog-body {
  color: #1a1a2e;
}

.failure-summary {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff7ed;
  color: #b45309;
  font-size: 14px;

  span {
    flex-shrink: 0;
    color: #8888aa;
  }
}

.failure-links {
  margin-top: 16px;

  .failure-links-title {
    margin-bottom: 8px;
    color: #4a4a6a;
    font-size: 14px;
    font-weight: 600;
  }

  ol {
    max-height: 260px;
    margin: 0;
    padding: 10px 10px 10px 30px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fafbfc;
  }

  li {
    margin-bottom: 8px;
    color: #4a4a6a;
    font-size: 13px;
    line-height: 1.5;
    word-break: break-all;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 640px) {
  .url-upload-page {
    padding: 20px 14px 24px;
  }

  .url-upload-container {
    padding: 20px 14px 24px;
  }

  .upload-header .subtitle {
    width: 100%;
    margin-left: 0;
  }

  .stats-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;

    .clear-all-btn {
      margin-left: 0;
      text-align: left;
    }
  }

  .input-row .btn-add {
    width: 100%;
    justify-content: center;
  }

  .upload-area {
    padding: 24px 16px;

    .icon {
      font-size: 34px;
    }
  }

  .list-header {
    flex-direction: column;
    align-items: stretch;

    .actions {
      justify-content: flex-start;
    }
  }

  .link-table {
    th,
    td {
      padding: 10px 12px;
      font-size: 13px;
    }
  }

  .link-text {
    font-size: 13px;
  }
}
</style>
